import { lazy, Suspense, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { VoiceAssistantProvider } from './context/VoiceAssistantContext';
import GlobalVoiceAssistant from './components/GlobalVoiceAssistant';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import ImpersonationBanner from './components/ImpersonationBanner';
import FirstTimeOnboardingBanner from './components/FirstTimeOnboardingBanner';
import ChangePasswordPrompt from './components/ChangePasswordPrompt';
import Login from './pages/Login';
import PrivateRoute from './components/PrivateRoute';
import './App.css';
import './styles/design-system.css';
import { AuthContext } from './context/AuthContext';
import Home from './pages/Home';
import usePageTracking from './hooks/usePageTracking';

// Pages load on demand: each role downloads only the screens it opens,
// instead of the whole app up front.
const TwoFactorSetup = lazy(() => import('./pages/TwoFactorSetup'));
const Profile = lazy(() => import('./pages/Profile'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const SuperAdminReports = lazy(() => import('./pages/SuperAdminReports'));
const Moderators = lazy(() => import('./pages/Moderators'));
const Sme = lazy(() => import('./pages/Sme'));
const Examiners = lazy(() => import('./pages/Examiners'));
const SuperAdminLedger = lazy(() => import('./pages/SuperAdminLedger'));
const SuperAdminCategorizedSets = lazy(() => import('./pages/SuperAdminCategorizedSets'));
const PaperManagement = lazy(() => import('./pages/PaperManagement'));
const CollegeAdminDashboard = lazy(() => import('./pages/CollegeAdminDashboard'));
const FacultyDashboard = lazy(() => import('./pages/FacultyDashboard'));
const HODDashboard = lazy(() => import('./pages/HODDashboard'));
const PaperTemplates = lazy(() => import('./pages/PaperTemplates'));
const ExtractTemplateFromPDF = lazy(() => import('./pages/ExtractTemplateFromPDF'));
const PDFLibrary = lazy(() => import('./pages/PDFLibrary')); // Re-enabled with simple text extraction
const MySubjects = lazy(() => import('./pages/MySubjects'));
const SMESelection = lazy(() => import('./pages/SMESelection'));
const SMEPapers = lazy(() => import('./pages/SMEPapers'));
const ModeratorCategorization = lazy(() => import('./pages/ModeratorCategorization'));
const ModeratorCategorizationView = lazy(() => import('./pages/ModeratorCategorizationView'));
const ModeratorApprovals = lazy(() => import('./pages/ModeratorApprovals'));
const StoredPapers = lazy(() => import('./pages/StoredPapers'));
const SubQuestionVariations = lazy(() => import('./pages/SubQuestionVariations'));
const SMESubQuestionReview = lazy(() => import('./pages/SMESubQuestionReview'));
const EduLabPDFManagement = lazy(() => import('./pages/EduLabPDFManagement'));
const SuperAdminSubjects = lazy(() => import('./pages/SuperAdminSubjects'));
const SuperAdminActivityLog = lazy(() => import('./pages/SuperAdminActivityLog'));
const SuperAdminQuestionInsights = lazy(() => import('./pages/SuperAdminQuestionInsights'));
const AIProviderSettings = lazy(() => import('./pages/AIProviderSettings'));
const CoordinatorPermissionsMatrix = lazy(() => import('./pages/CoordinatorPermissionsMatrix'));
const CoordinatorDashboard = lazy(() => import('./pages/CoordinatorDashboard'));
const UserManagementHub = lazy(() => import('./components/UserManagementHub'));
const SetPassword = lazy(() => import('./pages/SetPassword'));

function HomeRedirect() {
  const { user, loading } = useContext(AuthContext);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  // Guests see the public homepage by default (not the login page). They choose
  // to click "Login" themselves. Authenticated users are sent to their dashboard.
  if (!user) return <Home />;

  if (user.role === 'superadmin' || user.role === 'cetadmin') return <Navigate to="/super-admin" />;
  if (user.role === 'cetcoordinator') return <Navigate to="/coordinator" />;
  if (user.role === 'moderator') return <Navigate to="/moderator" />;
  if (user.role === 'subject_matter_expert') return <Navigate to="/subject-matter-expert" />;
  if (user.role === 'examiner') return <Navigate to="/examiner" />;

  return <Navigate to="/login" />;
}

function MainLayout() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  usePageTracking();

  const authPages = ['/login', '/register', '/update-password'];
  const showSidebar = user && !authPages.includes(location.pathname);
  // The public landing page ('/') has its own institutional sticky header, so the
  // global Navbar is suppressed there to avoid a stacked double-header.
  const showNavbar = !authPages.includes(location.pathname) && location.pathname !== '/';

  return (
    <>
      <ImpersonationBanner />
      {showNavbar && <Navbar />}
      <Sidebar />
      <div className={`main-content ${showSidebar ? 'with-sidebar' : ''} ${showNavbar ? '' : 'no-navbar'}`}>
        {showSidebar && <FirstTimeOnboardingBanner />}
        {showSidebar && <ChangePasswordPrompt />}
        <Suspense fallback={<div className="loading"><div className="spinner"></div></div>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* <Route path="/register" element={<Register />} /> */}
          <Route path="/set-password" element={<SetPassword />} />

          <Route path="/2fa-setup" element={
            <PrivateRoute>
              <TwoFactorSetup />
            </PrivateRoute>
          } />

          <Route path="/2fa-security" element={
            <PrivateRoute>
              <TwoFactorSetup />
            </PrivateRoute>
          } />

          <Route path="/profile" element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          } />

          {/* PDF Library - Simple text extraction, no embeddings */}
          <Route path="/pdf-library" element={
            <PrivateRoute>
              <PDFLibrary />
            </PrivateRoute>
          } />

          {/* My Subjects — assignment status + access requests (examiner/SME/moderator) */}
          <Route path="/my-subjects" element={
            <PrivateRoute allowedRoles={['examiner', 'subject_matter_expert', 'moderator']}>
              <MySubjects />
            </PrivateRoute>
          } />

          <Route path="/super-admin" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <SuperAdminDashboard />
            </PrivateRoute>
          } />

          <Route path="/super-admin/token-ledger" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <SuperAdminLedger />
            </PrivateRoute>
          } />

          <Route path="/super-admin/moderators" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <Moderators />
            </PrivateRoute>
          } />

          <Route path="/super-admin/sme" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <Sme />
            </PrivateRoute>
          } />

          <Route path="/super-admin/examiners" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <Examiners />
            </PrivateRoute>
          } />

          <Route path="/super-admin/reports" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <SuperAdminReports />
            </PrivateRoute>
          } />

          <Route path="/super-admin/edulab-pdfs" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <EduLabPDFManagement />
            </PrivateRoute>
          } />

          <Route path="/super-admin/subjects" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <SuperAdminSubjects />
            </PrivateRoute>
          } />

          <Route path="/super-admin/activity-log" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <SuperAdminActivityLog />
            </PrivateRoute>
          } />

          <Route path="/coordinator/token-ledger" element={
            <PrivateRoute role="cetcoordinator" requiredScopes={['nav.tokenledger']}>
              <SuperAdminLedger />
            </PrivateRoute>
          } />

          <Route path="/super-admin/categorized-sets" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <SuperAdminCategorizedSets />
            </PrivateRoute>
          } />

          <Route path="/coordinator/categorized-sets" element={
            <PrivateRoute role="cetcoordinator" requiredScopes={['nav.categorizedsets']}>
              <SuperAdminCategorizedSets />
            </PrivateRoute>
          } />

          <Route path="/super-admin/paper-management" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <PaperManagement />
            </PrivateRoute>
          } />

          <Route path="/coordinator/paper-management" element={
            <PrivateRoute role="cetcoordinator" requiredScopes={['papers.manage']}>
              <PaperManagement />
            </PrivateRoute>
          } />

          <Route path="/super-admin/question-insights" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <SuperAdminQuestionInsights />
            </PrivateRoute>
          } />

          <Route path="/super-admin/ai-provider" element={
            <PrivateRoute role="superadmin">
              <AIProviderSettings />
            </PrivateRoute>
          } />

          <Route path="/super-admin/user-management" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <UserManagementHub />
            </PrivateRoute>
          } />

          <Route path="/super-admin/coordinators/:id" element={
            <PrivateRoute allowedRoles={['superadmin', 'cetadmin']}>
              <CoordinatorPermissionsMatrix />
            </PrivateRoute>
          } />

          <Route path="/coordinator" element={
            <PrivateRoute role="cetcoordinator">
              <CoordinatorDashboard />
            </PrivateRoute>
          } />

          {/* Coordinator sub-views. Paths mirror the sidebar links in
              components/Sidebar.js exactly; each is gated by the same nav.*
              scope that controls the tab's visibility, so a coordinator can
              only reach a view they were granted. Components are reused from
              the admin surface — the coordinator sees a scope-limited slice. */}
          <Route path="/coordinator/users" element={
            <PrivateRoute role="cetcoordinator" requiredScopes={['nav.users']}>
              <UserManagementHub />
            </PrivateRoute>
          } />

          <Route path="/coordinator/subjects" element={
            <PrivateRoute role="cetcoordinator" requiredScopes={['nav.subjects']}>
              <SuperAdminSubjects />
            </PrivateRoute>
          } />

          <Route path="/coordinator/activity-log" element={
            <PrivateRoute role="cetcoordinator" requiredScopes={['nav.activity']}>
              <SuperAdminActivityLog />
            </PrivateRoute>
          } />

          <Route path="/coordinator/question-insights" element={
            <PrivateRoute role="cetcoordinator" requiredScopes={['nav.insights']}>
              <SuperAdminQuestionInsights />
            </PrivateRoute>
          } />

          <Route path="/moderator" element={
            <PrivateRoute role="moderator">
              <CollegeAdminDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner" element={
            <PrivateRoute role="examiner">
              <FacultyDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner/create" element={
            <PrivateRoute role="examiner">
              <FacultyDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner/papers" element={
            <PrivateRoute role="examiner">
              <FacultyDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner/view/:paperId" element={
            <PrivateRoute role="examiner">
              <FacultyDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner/papers/:paper_id/sub-questions" element={
            <PrivateRoute role="examiner">
              <SubQuestionVariations />
            </PrivateRoute>
          } />

          <Route path="/paper-templates" element={
            <PrivateRoute role="examiner">
              <PaperTemplates />
            </PrivateRoute>
          } />

          <Route path="/extract-template" element={
            <PrivateRoute role="examiner">
              <ExtractTemplateFromPDF />
            </PrivateRoute>
          } />

          <Route path="/subject-matter-expert" element={
            <PrivateRoute role="subject_matter_expert">
              <HODDashboard />
            </PrivateRoute>
          } />

          <Route path="/sme-selection" element={
            <PrivateRoute role="subject_matter_expert">
              <SMESelection />
            </PrivateRoute>
          } />

          <Route path="/sme-papers" element={
            <PrivateRoute role="subject_matter_expert">
              <SMEPapers />
            </PrivateRoute>
          } />

          <Route path="/sme-papers/view/:paperId" element={
            <PrivateRoute role="subject_matter_expert">
              <SMEPapers />
            </PrivateRoute>
          } />

          <Route path="/sme/sub-question-reviews" element={
            <PrivateRoute role="subject_matter_expert">
              <SMESubQuestionReview />
            </PrivateRoute>
          } />

          <Route path="/moderator-categorization" element={
            <PrivateRoute role="moderator">
              <ModeratorCategorization />
            </PrivateRoute>
          } />

          <Route path="/moderator-categorization/view/:paperId" element={
            <PrivateRoute role="moderator">
              <ModeratorCategorization />
            </PrivateRoute>
          } />

          <Route path="/moderator-categorization/view/:paperId/category/:categoryKey" element={
            <PrivateRoute role="moderator">
              <ModeratorCategorizationView />
            </PrivateRoute>
          } />

          <Route path="/moderator/approvals" element={
            <PrivateRoute role="moderator">
              <ModeratorApprovals />
            </PrivateRoute>
          } />

          <Route path="/moderator/stored-papers" element={
            <PrivateRoute role="moderator">
              <StoredPapers />
            </PrivateRoute>
          } />

          <Route path="/" element={<HomeRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </div>
      <GlobalVoiceAssistant />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <PermissionsProvider>
            <Router>
              <VoiceAssistantProvider>
                <div className="app">
                  <MainLayout />
                </div>
              </VoiceAssistantProvider>
            </Router>
          </PermissionsProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
